import React from "react";
import { MobXProviderContext } from "mobx-react";
import { Button } from "primereact/button";
import { useNavigate } from "react-router-dom";
import { useContext } from "react";
import { RouterAddress } from "../../constants/RouterAddress";
import { getWebsiteUrlForHome, phoneNumberFormatter } from "../../utils/stringUtils";
import { BusinessHomeProfile } from "../../models/businessHomeProfile";
import {
  appAccentPanelStyle,
  appMutedTextStyle,
  appSectionCardStyle,
} from "./uiSurfaceStyles";
import { useAuthExperience } from "../../features/auth/hooks/useAuthExperience";
import { BRAND_KEY_ONE_HUB_FOR_CARE } from "../../constants/brandConstants";

interface HomeProfileProps {
  homeData: BusinessHomeProfile | null;
  onEditClick?: () => void;
  onDeleteClick?: () => void;
}

const HomeProfile: React.FC<HomeProfileProps> = ({
  homeData,
  onEditClick,
  onDeleteClick,
}) => {
  const navigate = useNavigate();
  const authExperience = useAuthExperience();
  const { authStore } = useContext(MobXProviderContext);
  const phoneNumber = phoneNumberFormatter(homeData?.businessPhoneNumber?.toString()) || "Not provided";
  const address = homeData?.formattedAddress || "Address not available";
  const websiteStatusLabel = homeData?.isWebsiteActive ? "Website Live" : "Website Not Published";
  const facilityLabel = authExperience.brandKey === BRAND_KEY_ONE_HUB_FOR_CARE ? "Facility" : "Home";
  const currentFacilityType = String(homeData?.facilityType || authStore.getUserFacilityType() || authExperience.providerSignup.defaultFacilityType || "").trim().toLowerCase();
  const currentFacilityTypeLabel = authExperience.facilityTypeOptions.find((option) => option.value === currentFacilityType)?.label
    || authExperience.facilityTypeOptions.find((option) => option.value === authExperience.providerSignup.defaultFacilityType)?.label
    || "Not set";

  return (
    <div style={appSectionCardStyle} className="p-3">
      <div style={{ ...appAccentPanelStyle, padding: "1rem 1.1rem" }}>
        <div className="flex flex-wrap align-items-start justify-content-between gap-3">
          <div className="flex align-items-start gap-3">
            <div
              style={{
                width: "3.4rem",
                height: "3.4rem",
                borderRadius: "999px",
                background: "linear-gradient(135deg, #185DA2 0%, #2f80c5 100%)",
                color: "#ffffff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.2rem",
                fontWeight: 700,
                boxShadow: "0 12px 22px rgba(24, 93, 162, 0.2)",
                flexShrink: 0,
              }}
            >
              {(homeData?.businessName || "H").charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="flex flex-wrap align-items-center gap-2">
                <div style={{ fontSize: "1.2rem", fontWeight: 700, color: "#0f172a" }}>
                  {homeData?.businessName || `Unnamed ${facilityLabel}`}
                </div>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    padding: "0.35rem 0.7rem",
                    borderRadius: "999px",
                    background: homeData?.isWebsiteActive ? "#ecfdf5" : "#fff7ed",
                    border: homeData?.isWebsiteActive ? "1px solid #bbf7d0" : "1px solid #fed7aa",
                    color: homeData?.isWebsiteActive ? "#166534" : "#9a3412",
                    fontSize: "0.82rem",
                    fontWeight: 700,
                  }}
                >
                  {websiteStatusLabel}
                </span>
              </div>
              <div className="mt-2" style={appMutedTextStyle}>
                {address}
              </div>
              <div className="mt-2 flex flex-wrap align-items-center gap-3" style={appMutedTextStyle}>
                <span className="flex align-items-center gap-2">
                  <i className="pi pi-building" style={{ color: "#185DA2" }} />
                  <strong style={{ color: "#0f172a" }}>{authExperience.providerHome.facilityTypeLabel}:</strong>
                  <span>{currentFacilityTypeLabel}</span>
                </span>
                <span className="flex align-items-center gap-2">
                  <i className="pi pi-phone" style={{ color: "#185DA2" }} />
                  {phoneNumber}
                </span>
                {homeData?.isWebsiteActive ? (
                  <a
                    href={getWebsiteUrlForHome(homeData)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.4rem",
                      color: "#b45309",
                      fontWeight: 600,
                      textDecoration: "none",
                    }}
                  >
                    <span>Open Public Website</span>
                    <i className="pi pi-external-link" style={{ fontSize: "0.75rem" }} />
                  </a>
                ) : null}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button severity="warning" label="Edit" rounded className="p-button-outlined" onClick={() => {
              onEditClick && onEditClick();
            }} />
            <Button severity="warning" label="Binder" rounded className="p-button-outlined" onClick={() => {
              navigate(`${RouterAddress.getHomeBinderPagePath(homeData?.homeId)}`);
            }} />
            {onDeleteClick ? (
              <Button severity="danger" label="Delete" rounded className="p-button-outlined" onClick={() => {
                onDeleteClick && onDeleteClick();
              }} />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeProfile;
